import { motion } from 'framer-motion';

interface FanOutPicsProps {
    image1?: React.ReactNode;
    image2?: React.ReactNode;
    image3?: React.ReactNode;
}

interface CardConfig {
    image?: React.ReactNode;
    xOffset: number;
    yOffset: number;
    rotation: number;
    delay: number;
    duration: number;
    gradientClass: string;
    zIndex: number;
}

interface AnimatedCardProps {
    config: CardConfig;
}

const AnimatedCard = ({ config }: AnimatedCardProps) => {
    const { image, xOffset, yOffset, rotation, delay, duration, gradientClass, zIndex } = config;

    return (
        <motion.div
            className="absolute w-48 h-60 rounded-lg overflow-hidden"
            style={{
                left: '50%',
                bottom: '15%',
                transformOrigin: 'center bottom',
                zIndex,
            }}
            initial={{ x: '-50%', y: 0, rotate: 0, scale: 0.9, opacity: 0 }}
            animate={{
                x: xOffset === 0 ? '-50%' : `calc(-50% + ${xOffset}px)`,
                y: yOffset,
                rotate: rotation,
                opacity: 1,
                scale: 1,
                transition: {
                    duration,
                    delay,
                    ease: "easeOut"
                }
            }}
        >
            {image ? (
                <div className="w-full h-full flex items-center justify-center overflow-hidden rounded-[24px] border-2 border-osGrey-4">
                    {image}
                </div>
            ) : (
                <div className={`w-full h-full ${gradientClass}`} />
            )}
        </motion.div>
    );
};

const animationDuration = 0.4;

export const FanOutPics = ({
    image1,
    image2,
    image3,
}: FanOutPicsProps) => {
    const cardConfigs: CardConfig[] = [
        {
            image: image1,
            xOffset: -30,
            yOffset: 15,
            rotation: -15,
            delay: 0.2,
            duration: animationDuration,
            gradientClass: "bg-gradient-to-br from-blue-400 to-blue-600",
            zIndex: 3
        },
        {
            image: image2,
            xOffset: 0,
            yOffset: 0,
            rotation: 0,
            delay: 0.1,
            duration: animationDuration + 0.2,
            gradientClass: "bg-gradient-to-br from-green-400 to-green-600",
            zIndex: 2
        },
        {
            image: image3,
            xOffset: 30,
            yOffset: 15,
            rotation: 15,
            delay: 0.3,
            duration: animationDuration,
            gradientClass: "bg-gradient-to-br from-purple-400 to-purple-600",
            zIndex: 1
        }
    ];

    return (
        <div className="relative flex justify-center items-center min-h-[300px] p-8">
                {cardConfigs.map((config, index) => (
                    <AnimatedCard key={index} config={config} />
                ))}
        </div>
    );
};